import { ReactNode } from "react";
import { Heart, MoreVertical, ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ContactFormSheet } from "@/components/ContactFormSheet";
import { FeatureRequestSheet } from "@/components/FeatureRequestSheet";
import { AboutSheet } from "@/components/AboutSheet";
import { SocialShare } from "@/components/SocialShare";

interface MobilePlayerLayoutProps {
  children: ReactNode;
  videoUrl: string | null;
  videoTitle: string;
}

export const MobilePlayerLayout = ({ children, videoUrl, videoTitle }: MobilePlayerLayoutProps) => {
  return (
    <div className="fixed inset-0 flex flex-col">
      {/* Video Background - fills entire screen */}
      {videoUrl && (
        <div className="absolute inset-0 -z-10">
          <video
            className="w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            src={videoUrl}
            aria-label={videoTitle}
          >
            <track kind="captions" />
          </video>
          {/* Gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background/90" />
        </div>
      )}

      {/* Player Content - centered with glass effect */}
      <div className="flex-1 flex items-center justify-center p-4">
        {children}
      </div>
    </div>
  );
};

export const MobileMenuButton = () => {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-8 w-8"
          aria-label="Open menu"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="glass-card border-l border-border/50 w-72">
        <SheetHeader>
          <SheetTitle>Menu</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-3 mt-4">
          <AboutSheet className="text-foreground w-full" />
          <SocialShare className="text-foreground w-full" />
          <Button
            variant="ghost"
            size="sm"
            className="glass-card group w-full hover:scale-105 smooth-transition"
            onClick={() => window.open("https://buymeacoffee.com/skylofiatc", "_blank")}
          >
            <Heart className="h-4 w-4 mr-2 group-hover:text-red-500 transition-colors" />
            Donate
          </Button>
          <FeatureRequestSheet className="text-foreground w-full" />
          <ContactFormSheet className="text-foreground w-full" />
        </div>
      </SheetContent>
    </Sheet>
  );
};

interface MobilePlayerCollapseButtonProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export const MobilePlayerCollapseButton = ({ isCollapsed, onToggle }: MobilePlayerCollapseButtonProps) => {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onToggle}
      className="h-8 w-8"
      aria-label={isCollapsed ? "Expand player" : "Collapse player"}
    >
      {isCollapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
    </Button>
  );
};
